import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPin, Gavel, Plus, ArrowLeftRight, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InlineLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { useMatchDetail } from "@/hooks/useMatches";
import { useCouponCart } from "@/stores/couponCart";
import { useUi } from "@/stores/ui";
import { BetTypeSheet } from "@/components/coupon/BetTypeSheet";
import { useState } from "react";
import { formatDate, formatTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { ApiEvent, ApiH2HItem, ApiLineup, ApiTeamStats, MatchDetail as MatchDetailData } from "@/types/match-detail";
import type { Match } from "@/types/domain";

const FINISHED = ["FT", "AET", "PEN"];
const NOT_STARTED = ["NS", "TBD", "PST"];
const CANCELED = ["CANC", "ABD"];

export function MatchDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const matchId = id ? Number(id) : undefined;
  const { data, isLoading, error } = useMatchDetail(matchId);

  if (isLoading) return <InlineLoading label={t("common.loading")} />;
  if (error || !data) {
    return (
      <div className="space-y-3">
        <BackHeader />
        <EmptyState title={t("common.error")} description={error instanceof Error ? error.message : undefined} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BackHeader />
      <Header data={data} />
      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="summary">{t("matchDetail.tabs.summary")}</TabsTrigger>
          <TabsTrigger value="lineups">{t("matchDetail.tabs.lineups")}</TabsTrigger>
          <TabsTrigger value="stats">{t("matchDetail.tabs.stats")}</TabsTrigger>
          <TabsTrigger value="h2h">{t("matchDetail.tabs.h2h")}</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-3">
          <Summary data={data} />
        </TabsContent>
        <TabsContent value="lineups" className="space-y-3">
          <LineupsView lineups={data.lineups} />
        </TabsContent>
        <TabsContent value="stats" className="space-y-3">
          <StatsView statistics={data.statistics} />
        </TabsContent>
        <TabsContent value="h2h" className="space-y-3">
          <H2HView items={data.h2h} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BackHeader() {
  return (
    <header className="flex items-center gap-3">
      <Button asChild variant="ghost" size="icon">
        <Link to="/matches"><ArrowLeft /></Link>
      </Button>
    </header>
  );
}

function Header({ data }: { data: MatchDetailData }) {
  const { fixture } = data;
  const status = fixture.fixture.status.short;
  const isLive = !NOT_STARTED.includes(status) && !FINISHED.includes(status) && !CANCELED.includes(status);
  const isFinished = FINISHED.includes(status);
  const isUpcoming = NOT_STARTED.includes(status);

  // Construit un Match minimal pour le BetTypeSheet
  const matchForBetting: Match = {
    id: fixture.fixture.id,
    league: { id: fixture.league.id, name: fixture.league.name, country: fixture.league.country, logo: fixture.league.logo },
    home: fixture.teams.home,
    away: fixture.teams.away,
    kickoff: fixture.fixture.date,
    status: status as Match["status"],
    score: fixture.goals,
    elapsed: fixture.fixture.status.elapsed,
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2 min-w-0">
            {fixture.league.logo && (
              <img src={fixture.league.logo} alt="" className="size-4 object-contain bg-white rounded" />
            )}
            <span className="truncate">{fixture.league.country} · {fixture.league.name}</span>
          </div>
          {fixture.league.round && <span className="shrink-0 truncate max-w-[40%]">{fixture.league.round}</span>}
        </div>

        <div className="flex items-center justify-between gap-3">
          <TeamBlock name={fixture.teams.home.name} logo={fixture.teams.home.logo} winner={fixture.teams.home.winner} />
          <ScoreBlock data={data} isLive={isLive} isFinished={isFinished} isUpcoming={isUpcoming} />
          <TeamBlock name={fixture.teams.away.name} logo={fixture.teams.away.logo} winner={fixture.teams.away.winner} align="end" />
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground pt-1 border-t">
          {fixture.fixture.venue.name && (
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <MapPin className="size-3 shrink-0" />
              <span className="truncate">{fixture.fixture.venue.name}{fixture.fixture.venue.city ? `, ${fixture.fixture.venue.city}` : ""}</span>
            </span>
          )}
          {fixture.fixture.referee && (
            <span className="inline-flex items-center gap-1.5">
              <Gavel className="size-3" /> {fixture.fixture.referee}
            </span>
          )}
        </div>

        {isUpcoming && <BettingButton match={matchForBetting} />}
      </CardContent>
    </Card>
  );
}

function ScoreBlock({ data, isLive, isFinished, isUpcoming }: { data: MatchDetailData; isLive: boolean; isFinished: boolean; isUpcoming: boolean }) {
  const { fixture } = data;
  const elapsed = fixture.fixture.status.elapsed;
  if (isUpcoming) {
    return (
      <div className="text-center px-2">
        <p className="text-xs text-muted-foreground">{formatDate(fixture.fixture.date)}</p>
        <p className="font-display text-2xl font-bold">{formatTime(fixture.fixture.date)}</p>
        <Badge variant="secondary" className="mt-1">À venir</Badge>
      </div>
    );
  }
  return (
    <div className="text-center px-2">
      <p className="font-display text-3xl font-extrabold tabular-nums">
        {fixture.goals.home ?? 0} <span className="text-muted-foreground">-</span> {fixture.goals.away ?? 0}
      </p>
      {isLive ? (
        <Badge variant="live">{elapsed ? `${elapsed}'` : fixture.fixture.status.short}</Badge>
      ) : isFinished ? (
        <Badge variant="secondary">{fixture.fixture.status.short}</Badge>
      ) : (
        <Badge variant="warning">{fixture.fixture.status.short}</Badge>
      )}
    </div>
  );
}

function TeamBlock({ name, logo, winner, align = "start" }: { name: string; logo: string; winner: boolean | null; align?: "start" | "end" }) {
  return (
    <div className={cn("flex flex-col items-center gap-2 flex-1 min-w-0", align === "end" && "")}>
      {logo ? (
        <img src={logo} alt="" className="size-14 object-contain bg-white rounded-full p-1" loading="lazy" />
      ) : (
        <div className="size-14 rounded-full bg-muted" />
      )}
      <p className={cn("text-sm font-semibold text-center line-clamp-2", winner === true && "text-success")}>{name}</p>
    </div>
  );
}

function BettingButton({ match }: { match: Match }) {
  const { type, hasMatch, removeBet } = useCouponCart();
  const { setCouponSheetOpen } = useUi();
  const [betOpen, setBetOpen] = useState(false);
  const inCart = hasMatch(match.id);
  function onClick() {
    if (!type) {
      setCouponSheetOpen(true);
      return;
    }
    if (inCart) {
      removeBet(match.id);
      return;
    }
    setBetOpen(true);
  }
  return (
    <>
      <Button onClick={onClick} variant={inCart ? "outline" : "default"} className="w-full">
        <Plus className="size-4" /> {inCart ? "Retirer du coupon" : "Ajouter au coupon"}
      </Button>
      <BetTypeSheet open={betOpen} onOpenChange={setBetOpen} match={match} />
    </>
  );
}

// --- Onglet Résumé ---------------------------------------------------------
function Summary({ data }: { data: MatchDetailData }) {
  const events = data.events;
  if (!events.length) {
    return <EmptyState title="Aucun événement" description="Le timeline apparaîtra une fois le match commencé." />;
  }
  const homeId = data.fixture.teams.home.id;
  return (
    <Card>
      <CardContent className="py-3 space-y-2">
        {events.map((e, i) => (
          <EventRow key={`${e.time.elapsed}-${i}`} event={e} isHome={e.team.id === homeId} />
        ))}
      </CardContent>
    </Card>
  );
}

function EventRow({ event, isHome }: { event: ApiEvent; isHome: boolean }) {
  const Icon = eventIcon(event);
  const minute = `${event.time.elapsed}${event.time.extra ? `+${event.time.extra}` : ""}'`;
  const text = event.player.name ?? event.detail;
  const sub = event.type === "subst" && event.assist.name ? `→ ${event.assist.name}` : event.assist.name ? `(${event.assist.name})` : event.detail;
  return (
    <div className={cn("flex items-center gap-3 py-1.5 border-b last:border-0", !isHome && "flex-row-reverse text-right")}>
      <span className="text-xs font-mono text-muted-foreground w-10 shrink-0">{minute}</span>
      <Icon className={cn("size-4 shrink-0", iconColor(event))} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{text}</p>
        {sub && sub !== text && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
      </div>
    </div>
  );
}

function eventIcon(e: ApiEvent) {
  if (e.type === "Goal") return BallIcon;
  if (e.type === "Card") return Square;
  if (e.type === "subst") return ArrowLeftRight;
  return Gavel;
}

function iconColor(e: ApiEvent): string {
  if (e.type === "Card") return e.detail.toLowerCase().includes("red") ? "text-destructive fill-destructive" : "text-warning fill-warning";
  if (e.type === "Goal") return "text-success";
  if (e.type === "subst") return "text-muted-foreground";
  return "text-muted-foreground";
}

function BallIcon({ className }: { className?: string }) {
  return <span className={cn("inline-block text-base", className)}>⚽</span>;
}

// --- Onglet Compositions ---------------------------------------------------
function LineupsView({ lineups }: { lineups: ApiLineup[] }) {
  if (!lineups.length) {
    return <EmptyState title="Compositions indisponibles" description="Publiées ~1h avant le coup d'envoi." />;
  }
  return (
    <div className="space-y-4">
      {lineups.map((l) => (
        <Card key={l.team.id}>
          <CardContent className="py-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                {l.team.logo && <img src={l.team.logo} alt="" className="size-6 object-contain bg-white rounded-full p-0.5" />}
                <p className="font-bold truncate">{l.team.name}</p>
              </div>
              {l.formation && <Badge variant="secondary">{l.formation}</Badge>}
            </div>
            {l.coach?.name && <p className="text-xs text-muted-foreground">Coach : {l.coach.name}</p>}
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Titulaires</p>
              <ul className="space-y-1">
                {l.startXI.map((p) => (
                  <PlayerRow key={p.player.id} number={p.player.number} name={p.player.name} pos={p.player.pos} />
                ))}
              </ul>
            </div>
            {l.substitutes?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Remplaçants</p>
                <ul className="space-y-1">
                  {l.substitutes.map((p) => (
                    <PlayerRow key={p.player.id} number={p.player.number} name={p.player.name} pos={p.player.pos} muted />
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PlayerRow({ number, name, pos, muted }: { number: number | null; name: string; pos: string | null; muted?: boolean }) {
  return (
    <li className={cn("flex items-center gap-3 text-sm", muted && "text-muted-foreground")}>
      <span className="size-6 rounded bg-muted text-xs font-mono flex items-center justify-center shrink-0">{number ?? "—"}</span>
      <span className="flex-1 truncate">{name}</span>
      {pos && <span className="text-xs text-muted-foreground">{pos}</span>}
    </li>
  );
}

// --- Onglet Statistiques ---------------------------------------------------
function StatsView({ statistics }: { statistics: ApiTeamStats[] }) {
  if (statistics.length < 2) {
    return <EmptyState title="Statistiques indisponibles" description="Disponibles pour les ligues majeures pendant et après le match." />;
  }
  const [home, away] = statistics;
  const types = home.statistics.map((s) => s.type);
  return (
    <Card>
      <CardContent className="py-3 space-y-2">
        {types.map((type) => {
          const h = home.statistics.find((s) => s.type === type)?.value;
          const a = away.statistics.find((s) => s.type === type)?.value;
          return <StatBar key={type} label={type} home={h} away={a} />;
        })}
      </CardContent>
    </Card>
  );
}

function StatBar({ label, home, away }: { label: string; home: number | string | null | undefined; away: number | string | null | undefined }) {
  const hNum = toNumber(home);
  const aNum = toNumber(away);
  const total = hNum + aNum;
  const hPct = total > 0 ? (hNum / total) * 100 : 50;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium tabular-nums">{home ?? "—"}</span>
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{away ?? "—"}</span>
      </div>
      <div className="flex h-1.5 gap-0.5 overflow-hidden rounded-full bg-muted">
        <div className="bg-primary transition-all" style={{ width: `${hPct}%` }} />
        <div className="bg-secondary-foreground/40 transition-all" style={{ width: `${100 - hPct}%` }} />
      </div>
    </div>
  );
}

function toNumber(v: number | string | null | undefined): number {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = Number(String(v).replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

// --- Onglet Head-to-Head ---------------------------------------------------
function H2HView({ items }: { items: ApiH2HItem[] }) {
  if (!items.length) return <EmptyState title="Aucun confrontation directe" />;
  return (
    <div className="space-y-2">
      {items.map((m) => (
        <Card key={m.fixture.id}>
          <CardContent className="py-3 space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="truncate">{m.league.name}</span>
              <span>{formatDate(m.fixture.date)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium truncate flex-1">{m.teams.home.name}</span>
              <span className="font-display font-bold tabular-nums">
                {m.goals.home ?? "—"} - {m.goals.away ?? "—"}
              </span>
              <span className="text-sm font-medium truncate flex-1 text-right">{m.teams.away.name}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
